import { CHARACTER_ACTIONS } from "@/character/actions";
import {
  GRID_COLUMNS,
  GRID_ROWS,
  TEMPLATE_FRAME_HEIGHT,
  TEMPLATE_FRAME_WIDTH,
} from "@/character/grid";
import type { PixelRect } from "@/home/sheet-crop";
import {
  clampSquareCropRect,
  exportPetFrameCrop,
} from "@/pet/pet-frame-crop";

const VIEWPORT_MAX = 420;
const CROP_SCREEN_RATIO = 0.78;
const ZOOM_MIN = 0.35;
const ZOOM_MAX = 8;
/** Extra pan past image edge (screen px) so alignment is not stuck at sy/sx = 0. */
const PAN_SLACK_PX = 72;

export type PetFrameCropSlotPicker = {
  initialRow: number;
  initialCol: number;
  isFilled: (row: number, col: number) => boolean;
  thumb: (row: number, col: number) => string | null;
  onSnap: (row: number, col: number, dataUrl: string) => void | Promise<void>;
};

export type PetFrameCropModalOptions = {
  title: string;
  subtitle?: string;
  sourceDataUrl: string;
  frameWidth?: number;
  frameHeight?: number;
  /** Multi-snap: pick slot grid, Snap stays open. */
  slotPicker?: PetFrameCropSlotPicker;
  /** Single-snap: save once and close. */
  onApply?: (dataUrl: string) => void;
  onDone?: () => void;
  onCancel?: () => void;
};

function nextSlot(row: number, col: number): { row: number; col: number } {
  let c = col + 1;
  let r = row;
  if (c >= GRID_COLUMNS) {
    c = 0;
    r += 1;
  }
  if (r >= GRID_ROWS) {
    r = 0;
    c = 0;
  }
  return { row: r, col: c };
}

function slotLabel(row: number, col: number): string {
  const action = CHARACTER_ACTIONS.find((a) => a.row === row);
  return `${action?.templateLabel ?? `แถว ${row + 1}`} · ${col + 1}`;
}

export function openPetFrameCropModal(options: PetFrameCropModalOptions): () => void {
  const frameWidth = options.frameWidth ?? TEMPLATE_FRAME_WIDTH;
  const frameHeight = options.frameHeight ?? TEMPLATE_FRAME_HEIGHT;
  const snapMode = Boolean(options.slotPicker);

  const overlay = document.createElement("div");
  overlay.className = "shadow-frame-crop-modal";
  overlay.setAttribute("role", "presentation");

  const slotPickerPlaceholder = snapMode
    ? `<div class="shadow-frame-crop-slot-picker-host" data-shadow-frame-crop-slot-host></div>`
    : "";

  const actionButtons = snapMode
    ? `<button type="button" class="btn btn-ghost" data-shadow-frame-crop-cancel>ยกเลิก</button>
        <button type="button" class="btn btn-ghost" data-shadow-frame-crop-done>เสร็จ</button>
        <button type="button" class="btn btn-primary" data-shadow-frame-crop-snap>Snap ลงช่อง</button>`
    : `<button type="button" class="btn btn-ghost" data-shadow-frame-crop-cancel>ยกเลิก</button>
        <button type="button" class="btn btn-primary" data-shadow-frame-crop-apply>บันทึกลงช่อง</button>`;

  overlay.innerHTML = `
    <button type="button" class="shadow-frame-crop-backdrop" aria-label="ปิด"></button>
    <div class="shadow-frame-crop-dialog" role="dialog" aria-modal="true" aria-labelledby="shadow-frame-crop-title">
      <h2 id="shadow-frame-crop-title" class="shadow-frame-crop-title">${escapeHtml(options.title)}</h2>
      ${
        options.subtitle
          ? `<p class="hint shadow-frame-crop-sub">${escapeHtml(options.subtitle)}</p>`
          : ""
      }
      <p class="hint shadow-frame-crop-hint" data-shadow-frame-crop-hint>${
        snapMode
          ? `ซูม · ลากภาพ · เลือกช่องด้านล่าง · Snap (${frameWidth}×${frameHeight}px)`
          : `ซูม · ลากเลื่อนภาพให้ตรงกรอบ · บันทึกเป็น ${frameWidth}×${frameHeight}px`
      }</p>
      <p class="hint shadow-frame-crop-target" data-shadow-frame-crop-target hidden></p>
      <div class="shadow-frame-crop-zoom-row">
        <label class="shadow-frame-crop-zoom-label" for="shadow-frame-crop-zoom">
          <span>ซูม</span>
          <input type="range" id="shadow-frame-crop-zoom" min="${Math.round(ZOOM_MIN * 100)}" max="${Math.round(ZOOM_MAX * 100)}" value="100" step="5" />
          <span class="shadow-frame-crop-zoom-value" data-shadow-frame-crop-zoom-value>100%</span>
        </label>
        <button type="button" class="btn btn-ghost btn-xs" data-shadow-frame-crop-zoom-fit>พอดีกรอบ</button>
      </div>
      <div class="shadow-frame-crop-canvas-wrap">
        <canvas class="shadow-frame-crop-canvas" aria-label="ซูมและเลื่อนภาพให้ตรงกรอบครอป"></canvas>
      </div>
      <div class="shadow-frame-crop-result">
        <span class="hint">ตัวอย่างในเซลล์</span>
        <canvas class="shadow-frame-crop-preview" width="${frameWidth}" height="${frameHeight}" aria-hidden="true"></canvas>
      </div>
      ${slotPickerPlaceholder}
      <p class="hint shadow-frame-crop-snap-status" data-shadow-frame-crop-snap-status role="status"></p>
      <div class="shadow-frame-crop-actions home-actions">
        ${actionButtons}
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  document.body.classList.add("has-shadow-frame-crop-modal");

  let viewZoom = 1;
  let panOffsetX = 0;
  let panOffsetY = 0;
  let baseScale = 1;
  let viewportSize = VIEWPORT_MAX;
  let dragging = false;
  let dragStart: { x: number; y: number; ox: number; oy: number } | null = null;
  let closed = false;
  let selectedRow = options.slotPicker?.initialRow ?? 0;
  let selectedCol = options.slotPicker?.initialCol ?? 0;

  const canvas = overlay.querySelector<HTMLCanvasElement>(".shadow-frame-crop-canvas");
  const slotHost = overlay.querySelector<HTMLElement>("[data-shadow-frame-crop-slot-host]");
  const targetEl = overlay.querySelector<HTMLElement>("[data-shadow-frame-crop-target]");
  const snapStatusEl = overlay.querySelector<HTMLElement>("[data-shadow-frame-crop-snap-status]");
  const previewCanvas = overlay.querySelector<HTMLCanvasElement>(".shadow-frame-crop-preview");
  const zoomInput = overlay.querySelector<HTMLInputElement>("#shadow-frame-crop-zoom");
  const zoomValueEl = overlay.querySelector<HTMLElement>("[data-shadow-frame-crop-zoom-value]");
  const img = new Image();
  img.decoding = "async";

  const close = () => {
    if (closed) return;
    closed = true;
    window.removeEventListener("resize", onResize);
    canvas?.removeEventListener("wheel", onWheel);
    document.body.classList.remove("has-shadow-frame-crop-modal");
    overlay.remove();
  };

  const cropScreenMetrics = () => {
    const cropScreenSize = viewportSize * CROP_SCREEN_RATIO;
    const cropScreenX = (viewportSize - cropScreenSize) / 2;
    const cropScreenY = (viewportSize - cropScreenSize) / 2;
    return { cropScreenX, cropScreenY, cropScreenSize };
  };

  const imageDrawScale = (): number => baseScale * viewZoom;

  const imageTopLeft = (): { x: number; y: number } => {
    if (!img.naturalWidth) return { x: 0, y: 0 };
    const s = imageDrawScale();
    const dw = img.naturalWidth * s;
    const dh = img.naturalHeight * s;
    return {
      x: (viewportSize - dw) / 2 + panOffsetX,
      y: (viewportSize - dh) / 2 + panOffsetY,
    };
  };

  const syncZoomUi = () => {
    if (zoomInput) zoomInput.value = String(Math.round(viewZoom * 100));
    if (zoomValueEl) zoomValueEl.textContent = `${Math.round(viewZoom * 100)}%`;
  };

  const computeBaseScale = () => {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (!iw || !ih) return 1;
    return Math.min(viewportSize / iw, viewportSize / ih) * 0.92;
  };

  const computeViewportSize = () => {
    const wrap = overlay.querySelector<HTMLElement>(".shadow-frame-crop-canvas-wrap");
    const pad = 12;
    const wrapW = wrap?.clientWidth ? wrap.clientWidth - pad : VIEWPORT_MAX;
    return Math.min(VIEWPORT_MAX, Math.max(220, Math.floor(wrapW)));
  };

  const imageCropRectRaw = (): PixelRect | null => {
    if (!img.naturalWidth) return null;
    const s = imageDrawScale();
    const { x: panX, y: panY } = imageTopLeft();
    const { cropScreenX, cropScreenY, cropScreenSize } = cropScreenMetrics();
    const side = cropScreenSize / s;
    return {
      sx: (cropScreenX - panX) / s,
      sy: (cropScreenY - panY) / s,
      sw: side,
      sh: side,
    };
  };

  const imageCropRectForExport = (): PixelRect | null => {
    const raw = imageCropRectRaw();
    if (!raw) return null;
    return clampSquareCropRect(raw, img.naturalWidth, img.naturalHeight);
  };

  const clampPanOffsets = () => {
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    if (!iw || !ih) return;
    const s = imageDrawScale();
    const dh = ih * s;
    const dw = iw * s;
    const { cropScreenX, cropScreenY, cropScreenSize } = cropScreenMetrics();
    const side = Math.min(cropScreenSize / s, iw, ih);
    const centerX = (viewportSize - dw) / 2;
    const centerY = (viewportSize - dh) / 2;

    const maxPanX = cropScreenX - centerX;
    const minPanX = cropScreenX - s * (iw - side) - centerX;
    const maxPanY = cropScreenY - centerY;
    const minPanY = cropScreenY - s * (ih - side) - centerY;

    panOffsetX = Math.min(
      maxPanX + PAN_SLACK_PX,
      Math.max(minPanX - PAN_SLACK_PX, panOffsetX),
    );
    panOffsetY = Math.min(
      maxPanY + PAN_SLACK_PX,
      Math.max(minPanY - PAN_SLACK_PX, panOffsetY),
    );
  };

  const paintPreview = () => {
    if (!previewCanvas || !img.naturalWidth) return;
    const rect = imageCropRectForExport();
    if (!rect) return;
    const dataUrl = exportPetFrameCrop(img, rect, frameWidth, frameHeight);
    const previewImg = new Image();
    previewImg.onload = () => {
      const ctx = previewCanvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, frameWidth, frameHeight);
      ctx.drawImage(previewImg, 0, 0, frameWidth, frameHeight);
    };
    previewImg.src = dataUrl;
  };

  const paint = (recalcLayout = false) => {
    if (!canvas || !img.naturalWidth) return;
    if (recalcLayout) {
      viewportSize = computeViewportSize();
      baseScale = computeBaseScale();
      canvas.width = viewportSize;
      canvas.height = viewportSize;
      canvas.style.width = `${viewportSize}px`;
      canvas.style.height = `${viewportSize}px`;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const iw = img.naturalWidth;
    const ih = img.naturalHeight;
    const s = imageDrawScale();
    const { x: panX, y: panY } = imageTopLeft();
    const dw = iw * s;
    const dh = ih * s;

    ctx.clearRect(0, 0, viewportSize, viewportSize);
    ctx.drawImage(img, panX, panY, dw, dh);

    const { cropScreenX, cropScreenY, cropScreenSize } = cropScreenMetrics();

    ctx.fillStyle = "rgba(0, 0, 0, 0.52)";
    ctx.fillRect(0, 0, viewportSize, viewportSize);
    ctx.clearRect(cropScreenX, cropScreenY, cropScreenSize, cropScreenSize);
    ctx.drawImage(img, panX, panY, dw, dh);

    const raw = imageCropRectRaw();
    if (raw) {
      const sx0 = Math.max(0, raw.sx);
      const sy0 = Math.max(0, raw.sy);
      const sx1 = Math.min(iw, raw.sx + raw.sw);
      const sy1 = Math.min(ih, raw.sy + raw.sh);
      const srcW = sx1 - sx0;
      const srcH = sy1 - sy0;
      if (srcW > 0 && srcH > 0) {
        const destX = cropScreenX + (sx0 - raw.sx) * s;
        const destY = cropScreenY + (sy0 - raw.sy) * s;
        ctx.drawImage(img, sx0, sy0, srcW, srcH, destX, destY, srcW * s, srcH * s);
      }
    }

    ctx.strokeStyle = "rgba(110, 200, 255, 0.95)";
    ctx.lineWidth = 2;
    ctx.strokeRect(
      cropScreenX + 0.5,
      cropScreenY + 0.5,
      cropScreenSize - 1,
      cropScreenSize - 1,
    );

    paintPreview();
  };

  const setZoom = (next: number, anchorScreen?: { x: number; y: number }) => {
    if (!img.naturalWidth || !canvas) return;
    const prev = viewZoom;
    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    if (clamped === prev) return;

    const s0 = baseScale * prev;
    const { x: panX0, y: panY0 } = imageTopLeft();
    const anchor = anchorScreen ?? {
      x: viewportSize / 2,
      y: viewportSize / 2,
    };
    const ix = (anchor.x - panX0) / s0;
    const iy = (anchor.y - panY0) / s0;

    viewZoom = clamped;
    const s1 = baseScale * viewZoom;
    const dw = img.naturalWidth * s1;
    const dh = img.naturalHeight * s1;
    const panX1 = anchor.x - ix * s1;
    const panY1 = anchor.y - iy * s1;
    panOffsetX = panX1 - (viewportSize - dw) / 2;
    panOffsetY = panY1 - (viewportSize - dh) / 2;

    clampPanOffsets();
    syncZoomUi();
    paint();
  };

  const zoomToFitCrop = () => {
    if (!img.naturalWidth) return;
    const { cropScreenSize } = cropScreenMetrics();
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    const targetScale = cropScreenSize / side;
    viewZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, targetScale / baseScale));
    panOffsetX = 0;
    panOffsetY = 0;
    clampPanOffsets();
    syncZoomUi();
    paint(true);
  };

  const onWheel = (event: WheelEvent) => {
    event.preventDefault();
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
    setZoom(viewZoom * factor, {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  const onPointerDown = (event: PointerEvent) => {
    if (!canvas) return;
    dragging = true;
    dragStart = {
      x: event.clientX,
      y: event.clientY,
      ox: panOffsetX,
      oy: panOffsetY,
    };
    canvas.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!dragging || !dragStart) return;
    panOffsetX = dragStart.ox + (event.clientX - dragStart.x);
    panOffsetY = dragStart.oy + (event.clientY - dragStart.y);
    clampPanOffsets();
    paint(false);
  };

  const endDrag = (event: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    dragStart = null;
    canvas?.releasePointerCapture(event.pointerId);
  };

  const onResize = () => {
    clampPanOffsets();
    paint(true);
  };

  const syncTargetLabel = () => {
    if (!snapMode || !targetEl) return;
    targetEl.hidden = false;
    targetEl.textContent = `ช่องปลายทาง: ${slotLabel(selectedRow, selectedCol)}`;
  };

  const renderSlotPicker = () => {
    if (!snapMode || !options.slotPicker || !slotHost) return;
    const picker = options.slotPicker;
    const rows = CHARACTER_ACTIONS
      .map((action) => {
        const cols = Array.from({ length: GRID_COLUMNS }, (_, col) => {
          const row = action.row;
          const filled = picker.isFilled(row, col);
          const thumb = picker.thumb(row, col);
          const sel = row === selectedRow && col === selectedCol;
          return `<button
            type="button"
            class="shadow-frame-crop-slot-btn${sel ? " shadow-frame-crop-slot-btn--sel" : ""}${filled ? " shadow-frame-crop-slot-btn--filled" : ""}"
            data-crop-slot-row="${row}"
            data-crop-slot-col="${col}"
            title="${escapeHtml(slotLabel(row, col))}"
            aria-pressed="${sel ? "true" : "false"}"
          >
            ${
              thumb
                ? `<img class="shadow-frame-crop-slot-thumb" src="${thumb}" alt="" width="28" height="28" />`
                : `<span class="shadow-frame-crop-slot-num">${col + 1}</span>`
            }
          </button>`;
        }).join("");
        return `<div class="shadow-frame-crop-slot-row">
          <span class="shadow-frame-crop-slot-row-label">${escapeHtml(action.templateLabel)}</span>
          <div class="shadow-frame-crop-slot-cells">${cols}</div>
        </div>`;
      })
      .join("");
    slotHost.innerHTML = `<div class="shadow-frame-crop-slot-picker" role="group" aria-label="เลือกช่องปลายทาง">${rows}</div>`;
    syncTargetLabel();
  };

  const setSelectedSlot = (row: number, col: number) => {
    selectedRow = row;
    selectedCol = col;
    renderSlotPicker();
  };

  const onSlotClick = (event: Event) => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>("[data-crop-slot-row]");
    if (!btn) return;
    const row = Number(btn.dataset.cropSlotRow);
    const col = Number(btn.dataset.cropSlotCol);
    if (!Number.isFinite(row) || !Number.isFinite(col)) return;
    setSelectedSlot(row, col);
  };

  const onCancel = () => {
    options.onCancel?.();
    close();
  };

  const onDone = () => {
    options.onDone?.();
    close();
  };

  const exportCropDataUrl = (): string | null => {
    const rect = imageCropRectForExport();
    if (!rect || !img.naturalWidth) return null;
    return exportPetFrameCrop(img, rect, frameWidth, frameHeight);
  };

  const onApply = () => {
    const dataUrl = exportCropDataUrl();
    if (!dataUrl || !options.onApply) return;
    options.onApply(dataUrl);
    close();
  };

  const onSnap = () => {
    if (!options.slotPicker) return;
    const dataUrl = exportCropDataUrl();
    if (!dataUrl) return;
    const row = selectedRow;
    const col = selectedCol;
    void Promise.resolve(options.slotPicker.onSnap(row, col, dataUrl)).then(() => {
      if (snapStatusEl) {
        snapStatusEl.textContent = `Snap ลง ${slotLabel(row, col)} แล้ว — ขยับภาพแล้ว Snap ช่องถัดไปได้`;
      }
      const next = nextSlot(row, col);
      setSelectedSlot(next.row, next.col);
    });
  };

  canvas?.addEventListener("pointerdown", onPointerDown);
  canvas?.addEventListener("pointermove", onPointerMove);
  canvas?.addEventListener("pointerup", endDrag);
  canvas?.addEventListener("pointercancel", endDrag);
  canvas?.addEventListener("wheel", onWheel, { passive: false });
  zoomInput?.addEventListener("input", () => {
    const pct = Number(zoomInput.value);
    if (!Number.isFinite(pct)) return;
    setZoom(pct / 100);
  });
  overlay.querySelector("[data-shadow-frame-crop-zoom-fit]")?.addEventListener("click", zoomToFitCrop);
  overlay.querySelector("[data-shadow-frame-crop-cancel]")?.addEventListener("click", onCancel);
  overlay.querySelector("[data-shadow-frame-crop-apply]")?.addEventListener("click", onApply);
  overlay.querySelector("[data-shadow-frame-crop-snap]")?.addEventListener("click", onSnap);
  overlay.querySelector("[data-shadow-frame-crop-done]")?.addEventListener("click", onDone);
  slotHost?.addEventListener("click", onSlotClick);
  overlay
    .querySelector(".shadow-frame-crop-backdrop")
    ?.addEventListener("click", snapMode ? onDone : onCancel);
  window.addEventListener("resize", onResize);

  if (snapMode) {
    renderSlotPicker();
  }

  img.onload = () => {
    viewZoom = 1;
    panOffsetX = 0;
    panOffsetY = 0;
    clampPanOffsets();
    syncZoomUi();
    paint(true);
  };
  img.onerror = () => {
    const hint = overlay.querySelector<HTMLElement>(".shadow-frame-crop-hint");
    if (hint) hint.textContent = "โหลดภาพไม่สำเร็จ";
  };
  img.src = options.sourceDataUrl;

  return close;
}

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}
